// src/pages/Invoice-page/Invoice.js
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FaFileInvoice, FaSearch, FaPrint } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import SiteLogo from '../../assets/images/SparkLogistics.jpg';

import BASE_URL from '../../api';

const API_BASE = `${BASE_URL}/api`;

const Invoice = () => {
  const token = localStorage.getItem('token');
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showGenerateForm, setShowGenerateForm] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    fromDate: '',
    toDate: '',
    clientId: ''
  });

  // ────── FETCH CLIENTS ──────
  const fetchClients = async () => {
    try {
      const res = await fetch(`${API_BASE}/master/customer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) setClients(json.data);
    } catch (err) { console.error('Client fetch error:', err); }
  };

  // ────── FETCH INVOICES ──────
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/getctrl/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) setInvoices(json.data);
    } catch (err) { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (token) { fetchClients(); fetchInvoices(); }
  }, [token]);

  // ────── GENERATE INVOICE ──────
  const handleGenerate = async () => {
    if (!formData.fromDate || !formData.toDate || !formData.clientId) {
      return toast.error('All fields are required');
    }
    try {
      const res = await fetch(`${API_BASE}/getctrl/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Invoice generated!');
        setShowGenerateForm(false);
        fetchInvoices();
      } else {
        toast.error(json.message);
      }
    } catch (err) { toast.error('Network error'); }
  };

  // ────── PROFESSIONAL PDF UI DESIGN (A4) ──────
  const handlePrint = (inv) => {
    try {
      const client = inv.clientId;
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;

      // 1. LOGO
      doc.addImage(SiteLogo, 'JPEG', 15, 5, 50, 35);

      // 2. COMPANY HEADER (Top Right)
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(12).setFont('helvetica', 'bold');
      doc.text('SPARK LOGISTICS SERVICES', pageWidth - margin, 16, { align: 'right' });

      doc.setFont('helvetica', 'normal').setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const headerLines = [
        'Head Office: Sector 45, Gurugram',
        'Contact: +91 9630254020', // Updated from your client data example
        'Email: support@sparkcargo.in',
        'GSTIN: 06ABCDE1234F1Z5'
      ];
      doc.text(headerLines, pageWidth - margin, 21, { align: 'right', lineHeightFactor: 1.5 });

      // 3. SEPARATOR
      doc.setDrawColor(0, 102, 204);
      doc.setLineWidth(0.5);
      doc.line(margin, 38, pageWidth - margin, 38);

      // 4. TAX INVOICE TITLE & INFO
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16).setFont('helvetica', 'bold').text('TAX INVOICE', margin, 48);

      doc.setFontSize(9).setFont('helvetica', 'normal');
      doc.text(`Invoice No : ${inv.invoiceNumber}`, margin, 56);
      doc.text(`Date : ${new Date(inv.invoiceDate).toLocaleDateString('en-GB')}`, margin, 61);
      doc.text(`Bill Month : ${inv.billMonth}`, margin, 66);

      // 5. BILL TO SECTION (Mapped from your JSON "clientId" object)
      const rightColX = 120;
      doc.setFont('helvetica', 'bold').setFontSize(10).text('BILL TO:', rightColX, 48);

      // Using companyName "TATA" and name "TCS" from your JSON
      const clientName = `${client?.companyName || ''} (${client?.name || ''})`.toUpperCase();
      const clientAddress = `${client?.address || ''}, ${client?.station || ''}, ${client?.pincode || ''}`;
      const clientGST = client?.gstNumber || 'N/A';

      doc.setFontSize(9).text(clientName, rightColX, 54);
      doc.setFont('helvetica', 'normal');

      const splitAddress = doc.splitTextToSize(clientAddress, 75);
      doc.text(splitAddress, rightColX, 59);

      const gstY = 59 + (splitAddress.length * 4.5) + 2;
      doc.setFont('helvetica', 'bold').text(`GSTIN: ${clientGST}`, rightColX, gstY);

      // 6. TABLE DATA MAPPING
      // Note: In your JSON, frieghtCharge, cgst, and sgst are 0. 
      // I'm using logical fallbacks if they are 0.
      autoTable(doc, {
        startY: 85,
        margin: { left: margin, right: margin },
        head: [['Description', 'HSN/SAC', 'Qty', 'Rate', 'CGST', 'SGST', 'Total']],
        body: [[
          inv.product || 'DOMESTIC EXPRESS',
          inv.hsnCode || '9968', // Common HSN for Courier
          inv.noOfConsignment || 0,
          `INR ${(inv.frieghtCharge || (inv.grandTotal / inv.noOfConsignment)).toFixed(2)}`,
          `${inv.cgst || 0}%`,
          `${inv.sgst || 0}%`,
          inv.grandTotal.toFixed(2)
        ]],
        theme: 'striped',
        headStyles: { fillColor: [0, 102, 204], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 4, halign: 'center' },
        columnStyles: {
          0: { halign: 'left', cellWidth: 50 },
          6: { halign: 'right', fontStyle: 'bold' }
        }
      });

      // 7. TOTALS
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11).setFont('helvetica', 'bold');
      doc.text(`Grand Total: ${inv.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, pageWidth - margin, finalY, { align: 'right' });

      // 8. FOOTER / SIGNATURE
      doc.setFontSize(8).setFont('helvetica', 'normal').setTextColor(100, 100, 100);
      doc.text('Notes:', margin, finalY + 15);
      const terms = inv.tAndC ? doc.splitTextToSize(inv.tAndC, 100) : 'Standard terms apply.';
      doc.text(terms, margin, finalY + 20);

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold').text('For SPARK LOGISTICS SERVICES', pageWidth - margin, finalY + 35, { align: 'right' });
      doc.line(pageWidth - 65, finalY + 52, pageWidth - margin, finalY + 52);
      doc.text('Authorised Signatory', pageWidth - margin, finalY + 57, { align: 'right' });

      doc.save(`Invoice_${inv.invoiceNumber}.pdf`);
      toast.success('Professional PDF Generated');
    } catch (err) {
      console.error("PDF Error: ", err);
      toast.error('Failed to generate PDF');
    }
  };

  // ────── SEARCH FILTER ──────
  const filtered = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    if (!s) return invoices;
    return invoices.filter(inv => {
      const clientName = inv.clientId?.name || '';
      const companyName = inv.clientId?.companyName || '';
      return (
        inv.invoiceNumber.toLowerCase().includes(s) ||
        clientName.toLowerCase().includes(s) ||
        companyName.toLowerCase().includes(s)
      );
    });
  }, [invoices, searchTerm]);

  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Invoice Master</h2>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Toolbar */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <div className="relative w-full md:w-80">
              <FaSearch className="absolute left-3 top-3 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search invoice or client..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none transition shadow-sm"
              />
            </div>

            <button
              onClick={() => setShowGenerateForm(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium shadow-sm flex items-center gap-2"
            >
              <FaFileInvoice /> Generate Invoice
            </button>
          </div>

          {/* Table UI - Consistent with Pincode/Manifest Style */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">S.No</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Invoice No</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Client / Company</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Billing Period</th>
                  <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">Grand Total</th>
                  <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm">
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-500 font-medium">Loading invoices...</td></tr>
                ) : paginated.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-400 font-medium">No records found</td></tr>
                ) : (
                  paginated.map((inv, i) => {
                    // S.No Logic: Search Persistence Fix
                    const sNo = searchTerm
                      ? invoices.findIndex(orig => orig._id === inv._id) + 1
                      : (currentPage - 1) * itemsPerPage + i + 1;

                    return (
                      <tr key={inv._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-gray-600 font-medium">{sNo}</td>
                        <td className="p-3">
                          <span className="font-bold text-blue-700">{inv.invoiceNumber}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800 uppercase">
                              {inv.clientId?.companyName || 'N/A'}
                            </span>
                            <span className="text-[11px] text-gray-500">
                              {inv.clientId?.name || ''}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 uppercase tracking-tighter">
                            {inv.billMonth}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="font-bold text-gray-900">₹{inv.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center">
                            <button
                              onClick={() => handlePrint(inv)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition border border-green-200"
                              title="Print Professional PDF"
                            >
                              <FaPrint size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <span className="text-sm text-gray-600 font-medium">
                Showing <span className="text-blue-600">{Math.min(filtered.length, (currentPage - 1) * itemsPerPage + 1)}</span> to <span className="text-blue-600">{Math.min(filtered.length, currentPage * itemsPerPage)}</span> of {filtered.length} entries
              </span>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
                  Previous
                </button>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        {/* FORM MODAL */}
        {showGenerateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl animate-in fade-in duration-200">
              <h2 className="text-xl font-bold mb-4">Generate New Invoice</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-500">CLIENT</label>
                  <select
                    className="w-full p-2 border rounded mt-1"
                    onChange={e => setFormData({ ...formData, clientId: e.target.value })}
                  >
                    <option value="">Select Client</option>
                    {clients.map(c => <option key={c._id} value={c._id}>{c.name} - {c.companyName}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-500">FROM DATE</label>
                    <input type="date" className="w-full p-2 border rounded mt-1" onChange={e => setFormData({ ...formData, fromDate: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500">TO DATE</label>
                    <input type="date" className="w-full p-2 border rounded mt-1" onChange={e => setFormData({ ...formData, toDate: e.target.value })} />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    onClick={() => setShowGenerateForm(false)}
                    className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerate}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoice;